import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './WelcomeScreenEditor.css'

// Resize + re-compress. PNG inputs stay PNG (preserving transparency); JPEG inputs stay JPEG.
function compressImage(dataUrl, maxPx = 1920, quality = 0.85) {
  return new Promise((resolve) => {
    const isPng = dataUrl.startsWith('data:image/png')
    const img   = new Image()
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.naturalWidth, img.naturalHeight))
      const canvas = document.createElement('canvas')
      canvas.width  = Math.round(img.naturalWidth  * scale)
      canvas.height = Math.round(img.naturalHeight * scale)
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
      resolve(isPng ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', quality))
    }
    img.src = dataUrl
  })
}

// ── Small reusable upload slot ────────────────────────────────────────────────
function ImageSlot({ label, aspectClass, preview, onFile, onRemove }) {
  const fileRef = useRef(null)
  const handleChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async (ev) => {
      const compressed = await compressImage(ev.target.result)
      onFile(compressed)
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }
  return (
    <div className="wse-slot">
      <p className="wse-slot-label">{label}</p>
      <div className={`wse-upload-zone ${aspectClass}`} onClick={() => fileRef.current?.click()}>
        {preview ? (
          <img src={preview} alt={label} className="wse-upload-preview" />
        ) : (
          <div className="wse-upload-placeholder">
            <span className="wse-upload-icon">🖼</span>
            <p>Klicken zum Hochladen</p>
          </div>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="wse-file-input" onChange={handleChange} />
      {preview && (
        <button className="btn btn-ghost wse-remove-btn" onClick={onRemove}>Bild entfernen</button>
      )}
    </div>
  )
}

// ── Main editor ───────────────────────────────────────────────────────────────
export default function WelcomeScreenEditor() {
  const { apiFetch, admin, isOwner } = useAuth()

  const [tenants,    setTenants]    = useState([])
  const [tenantId,   setTenantId]   = useState(isOwner ? '' : admin?.tenant_id)
  const [enabled,    setEnabled]    = useState(true)
  const [buttonText, setButtonText] = useState('')

  // Per-orientation slot state: { preview, newData, removeFlag }
  const [land, setLand] = useState({ preview: null, newData: null, remove: false })
  const [port, setPort] = useState({ preview: null, newData: null, remove: false })
  const [logo, setLogo] = useState({ preview: null, newData: null, remove: false })
  const [navLogo, setNavLogo] = useState({ preview: null, newData: null, remove: false })

  // Preview tab: 'landscape' | 'portrait'
  const [previewTab, setPreviewTab] = useState('landscape')

  const [saving, setSaving] = useState(false)
  const [msg,    setMsg]    = useState(null)

  // Load tenants for owner
  useEffect(() => {
    if (!isOwner) return
    apiFetch('/api/admin/tenants')
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setTenants(data)
          setTenantId(data[0].id)
        }
      })
      .catch(() => {})
  }, [isOwner])

  // Load config + existing images when tenant changes
  useEffect(() => {
    if (!tenantId) return
    const qs = isOwner ? `?tenantId=${tenantId}` : ''

    apiFetch(`/api/admin/welcome-config${qs}`)
      .then(r => r.json())
      .then(cfg => {
        setEnabled(cfg.enabled !== false)
        setButtonText(cfg.buttonText || '')
        setLand({ preview: null, newData: null, remove: false })
        setPort({ preview: null, newData: null, remove: false })
        setLogo({ preview: null, newData: null, remove: false })
        setNavLogo({ preview: null, newData: null, remove: false })

        if (cfg.hasLogo) {
          apiFetch(`/api/admin/welcome-image-preview?orientation=logo${isOwner ? `&tenantId=${tenantId}` : ''}`)
            .then(r => r.ok ? r.blob() : null)
            .then(blob => { if (blob) setLogo(p => ({ ...p, preview: URL.createObjectURL(blob) })) })
            .catch(() => {})
        }
        if (cfg.hasNavLogo) {
          apiFetch(`/api/admin/welcome-image-preview?orientation=nav_logo${isOwner ? `&tenantId=${tenantId}` : ''}`)
            .then(r => r.ok ? r.blob() : null)
            .then(blob => { if (blob) setNavLogo(p => ({ ...p, preview: URL.createObjectURL(blob) })) })
            .catch(() => {})
        }
        if (cfg.hasLandscapeImage) {
          apiFetch(`/api/admin/welcome-image-preview?orientation=landscape${isOwner ? `&tenantId=${tenantId}` : ''}`)
            .then(r => r.ok ? r.blob() : null)
            .then(blob => { if (blob) setLand(p => ({ ...p, preview: URL.createObjectURL(blob) })) })
            .catch(() => {})
        }
        if (cfg.hasPortraitImage) {
          apiFetch(`/api/admin/welcome-image-preview?orientation=portrait${isOwner ? `&tenantId=${tenantId}` : ''}`)
            .then(r => r.ok ? r.blob() : null)
            .then(blob => { if (blob) setPort(p => ({ ...p, preview: URL.createObjectURL(blob) })) })
            .catch(() => {})
        }
      })
      .catch(() => {})
  }, [tenantId])

  const handleSave = async () => {
    setSaving(true)
    setMsg(null)
    try {
      const base = isOwner ? { tenantId } : {}

      const uploadImage = async (dataUrl, orientation) => {
        const r = await apiFetch('/api/admin/welcome-image', {
          method: 'POST',
          body: JSON.stringify({ ...base, orientation, imageDataUrl: dataUrl }),
        })
        if (!r.ok) {
          const err = await r.json().catch(() => ({}))
          throw new Error(`${orientation === 'landscape' ? 'Querformat' : 'Hochformat'}: ${err.error || r.status}`)
        }
      }

      if (land.newData)    { await uploadImage(land.newData, 'landscape');    setLand(p => ({ ...p, newData: null })) }
      if (port.newData)    { await uploadImage(port.newData, 'portrait');     setPort(p => ({ ...p, newData: null })) }
      if (logo.newData)    { await uploadImage(logo.newData, 'logo');         setLogo(p => ({ ...p, newData: null })) }
      if (navLogo.newData) { await uploadImage(navLogo.newData, 'nav_logo');  setNavLogo(p => ({ ...p, newData: null })) }

      // Save text config (and removal flags)
      const r = await apiFetch('/api/admin/welcome-config', {
        method: 'PUT',
        body: JSON.stringify({
          ...base,
          enabled,
          buttonText,
          removeLandscape: land.remove,
          removePortrait:  port.remove,
          removeLogo:      logo.remove,
          removeNavLogo:   navLogo.remove,
        }),
      })
      if (!r.ok) throw new Error('Speichern fehlgeschlagen')

      if (land.remove)    setLand(p => ({ ...p, remove: false, preview: null }))
      if (port.remove)    setPort(p => ({ ...p, remove: false, preview: null }))
      if (logo.remove)    setLogo(p => ({ ...p, remove: false, preview: null }))
      if (navLogo.remove) setNavLogo(p => ({ ...p, remove: false, preview: null }))

      setMsg({ type: 'success', text: 'Gespeichert.' })
    } catch (err) {
      setMsg({ type: 'error', text: err.message })
    } finally {
      setSaving(false)
      setTimeout(() => setMsg(null), 4000)
    }
  }

  const activePreview = previewTab === 'landscape' ? land.preview : port.preview

  return (
    <div>
      <div className="page-header">
        <h1>Willkommensbildschirm</h1>
        <p>Hintergrundbilder und Button-Text für den Startbildschirm am Kiosk.</p>
      </div>

      {isOwner && tenants.length > 0 && (
        <div className="card wse-tenant-card">
          <div className="form-group wse-tenant-form-group">
            <label>Mandant</label>
            <select className="form-control" value={tenantId} onChange={e => setTenantId(e.target.value)}>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
            </select>
          </div>
        </div>
      )}

      <div className="wse-layout">
        {/* ── Left: uploads + button text ── */}
        <div className="card wse-panel">
          <div className="row-between wse-toggle-row">
            <div>
              <strong className="wse-toggle-label">Willkommensbildschirm aktiv</strong>
              <p className="wse-toggle-desc">Deaktiviert: Kiosk startet direkt mit der Sprachauswahl.</p>
            </div>
            <label className="toggle">
              <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
              <span className="toggle-slider" />
            </label>
          </div>

          <p className="section-title">Hintergrundbilder</p>

          <div className="wse-slots-row">
            <ImageSlot
              label="Querformat (16:9)"
              aspectClass="wse-aspect-landscape"
              preview={land.remove ? null : land.preview}
              onFile={data => setLand({ preview: data, newData: data, remove: false })}
              onRemove={() => setLand({ preview: null, newData: null, remove: true })}
            />
            <ImageSlot
              label="Hochformat (9:16)"
              aspectClass="wse-aspect-portrait"
              preview={port.remove ? null : port.preview}
              onFile={data => setPort({ preview: data, newData: data, remove: false })}
              onRemove={() => setPort({ preview: null, newData: null, remove: true })}
            />
          </div>

          <p className="section-title wse-section-title-row">Logo <span className="wse-section-title-sub">(oben rechts)</span></p>
          <p className="wse-slot-hint">PNG mit transparentem Hintergrund empfohlen.</p>
          <ImageSlot
            label="Logo"
            aspectClass="wse-aspect-logo"
            preview={logo.remove ? null : logo.preview}
            onFile={data => setLogo({ preview: data, newData: data, remove: false })}
            onRemove={() => setLogo({ preview: null, newData: null, remove: true })}
          />

          <p className="section-title wse-section-title-row">Logo für andere Seiten</p>
          <p className="wse-slot-hint">
            Wird statt des viGuard-Logos oben in der Navigationsleiste auf allen
            Seiten nach dem Willkommensbildschirm angezeigt. Leer = viGuard-Logo.
            PNG mit transparentem Hintergrund empfohlen.
          </p>
          <ImageSlot
            label="Logo für andere Seiten"
            aspectClass="wse-aspect-logo"
            preview={navLogo.remove ? null : navLogo.preview}
            onFile={data => setNavLogo({ preview: data, newData: data, remove: false })}
            onRemove={() => setNavLogo({ preview: null, newData: null, remove: true })}
          />

          <p className="section-title wse-section-title-row">Button</p>
          <div className="form-group">
            <label>Button-Text</label>
            <input
              className="form-control"
              value={buttonText}
              onChange={e => setButtonText(e.target.value)}
              placeholder="Check-in"
            />
          </div>

          {msg && (
            <div className={`alert ${msg.type === 'success' ? 'alert-success' : 'alert-error'}`}>
              {msg.text}
            </div>
          )}

          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner spinner-sm" /> Speichern…</> : 'Speichern'}
          </button>
        </div>

        {/* ── Right: live preview ── */}
        <div className="wse-preview-panel">
          <div className="wse-preview-tabs">
            <button
              className={`wse-tab${previewTab === 'landscape' ? ' wse-tab--active' : ''}`}
              onClick={() => setPreviewTab('landscape')}
            >
              Querformat
            </button>
            <button
              className={`wse-tab${previewTab === 'portrait' ? ' wse-tab--active' : ''}`}
              onClick={() => setPreviewTab('portrait')}
            >
              Hochformat
            </button>
          </div>

          <div
            className={`wse-preview-screen ${previewTab === 'portrait' ? 'wse-preview-screen--portrait' : ''}`}
            style={activePreview && !(previewTab === 'landscape' ? land.remove : port.remove)
              ? { backgroundImage: `url(${activePreview})` }
              : undefined}
          >
            <div className="wse-preview-overlay" />
            {logo.preview && !logo.remove && (
              <img src={logo.preview} alt="Logo" className="wse-preview-logo" />
            )}
            <div className="wse-preview-content">
              <div className="wse-preview-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
                {buttonText || 'Check-in'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
